"use client";
import { useState } from "react";
import { saveAs } from "file-saver";
import * as XLSX from "xlsx";
import * as pdfjsLib from "pdfjs-dist";
import axios from "axios";

pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const API_BASE = "https://erp-xe9u.onrender.com/api";

export default function ConditionPdfFinalExport() {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState("");
  const [result, setResult] = useState(null);

  // ═══════════════════════════════════════════════════════
  //  PDF EXTRACTION LAYER
  // ═══════════════════════════════════════════════════════

  const COL_BOUNDS = [
    { key: "no", min: 0, max: 30 },
    { key: "section", min: 30, max: 60 },
    { key: "matCode", min: 60, max: 100 },
    { key: "laborCode", min: 100, max: 145 },
    { key: "layers", min: 145, max: 180 },
    { key: "quantity", min: 180, max: 211 },
    { key: "uom", min: 211, max: 228 },
    { key: "size", min: 228, max: 270 },
    { key: "materialCost", min: 270, max: 360 },
    { key: "laborCost", min: 360, max: 395 },
    { key: "crewHours", min: 395, max: 440 },
    { key: "matTotal", min: 440, max: 485 },
    { key: "labTotal", min: 485, max: 537 },
    { key: "itemTotal", min: 537, max: 600 },
  ];

  const assignColumn = (x0) => {
    for (const col of COL_BOUNDS) {
      if (x0 >= col.min && x0 < col.max) return col.key;
    }
    let nearest = COL_BOUNDS[0];
    let minDist = Infinity;
    for (const col of COL_BOUNDS) {
      const mid = (col.min + col.max) / 2;
      const dist = Math.abs(x0 - mid);
      if (dist < minDist) {
        minDist = dist;
        nearest = col;
      }
    }
    return nearest.key;
  };

  const parseNum = (s) => {
    if (!s) return null;
    const n = parseFloat(s.toString().replace(/,/g, "").trim());
    return isNaN(n) ? null : n;
  };

  /** Strip the duplicate dimension in parentheses.
   *  "4'x12' ( 4'  x 12' )"  →  "4'x12'"
   *  "Fine thread 1-1/4\""   →  "Fine thread 1-1/4\""  (no parens → unchanged)
   *  "4'x8' (4' x 8')"       →  "4'x8'"
   */
  const cleanSize = (raw = "") => {
    const match = raw.match(/\(\s*([^)]+?)\s*\)/);
    return match ? match[1].trim() : raw.trim();
  };

  const cleanItemCode = (code = "") =>
    code
      .replace(/\s+/g, "") // remove all whitespace
      .trim()
      .toUpperCase();

  const extractPageTextItems = async (page) => {
    const textContent = await page.getTextContent();
    const viewport = page.getViewport({ scale: 1.0 });
    const items = [];
    for (const item of textContent.items) {
      if (!item.str?.trim()) continue;
      const tx = item.transform;
      items.push({
        text: item.str.trim(),
        x0: tx[4],
        y0: viewport.height - tx[5],
        width: item.width,
      });
    }
    return items;
  };

  const parseMetadataFromItems = (textItems) => {
    const sorted = [...textItems].sort((a, b) => a.y0 - b.y0 || a.x0 - b.x0);
    const lineMap = new Map();
    for (const item of sorted) {
      const yKey = Math.round(item.y0 / 4) * 4;
      if (!lineMap.has(yKey)) lineMap.set(yKey, []);
      lineMap.get(yKey).push(item);
    }
    const lines = [...lineMap.entries()]
      .sort(([a], [b]) => a - b)
      .map(([, items]) => ({
        text: items
          .sort((a, b) => a.x0 - b.x0)
          .map((i) => i.text)
          .join(" "),
      }));

    const meta = { conditionNo: "", name: "", area: "" };
    for (const line of lines) {
      const t = line.text;
      let m = t.match(/Condition No\.:\s*(\S+)/);
      if (m) meta.conditionNo = m[1];
      m = t.match(/Name:\s*(.+?)(?:\s+\d{2}\s+\d{2}\s+\d{2}|$)/);
      if (m) meta.name = m[1].trim();
      m = t.match(/Area:\s*(.+?)(?:\s+\d{2}\s+\d{2}\s+\d{2}|$)/);
      if (m) meta.area = m[1].trim();
    }
    return meta;
  };

  const parseTableFromItems = (textItems) => {
    const sorted = [...textItems].sort((a, b) => a.y0 - b.y0 || a.x0 - b.x0);
    const lineMap = new Map();
    for (const item of sorted) {
      const yKey = Math.round(item.y0 / 3) * 3;
      if (!lineMap.has(yKey)) lineMap.set(yKey, []);
      lineMap.get(yKey).push(item);
    }

    let headerY = null,
      subHeaderY = null;
    for (const [yKey, items] of lineMap.entries()) {
      const lt = items.map((i) => i.text).join(" ");
      if (
        lt.includes("No.") &&
        lt.includes("Section") &&
        lt.includes("Mat. Code")
      )
        headerY = yKey;
    }
    if (headerY === null) return [];

    for (const [yKey, items] of lineMap.entries()) {
      if (yKey > headerY && yKey < headerY + 20) {
        if (
          items
            .map((i) => i.text)
            .join(" ")
            .includes("Hours")
        ) {
          subHeaderY = yKey;
          break;
        }
      }
    }
    if (!subHeaderY) subHeaderY = headerY;

    let totalY = null;
    for (const [yKey, items] of [...lineMap.entries()].sort(
      ([a], [b]) => a - b,
    )) {
      if (yKey <= subHeaderY) continue;
      const lt = items.map((i) => i.text).join(" ");
      if (items.length > 0 && items[0].text === "Total" && items[0].x0 > 350)
        totalY = yKey;
      if (/^\d{2}\/\d{2}\/\d{4}/.test(lt)) break;
    }

    const dataItems = sorted.filter((item) => {
      const yKey = Math.round(item.y0 / 3) * 3;
      return yKey > subHeaderY && (!totalY || yKey < totalY);
    });

    const dataLines = new Map();
    for (const item of dataItems) {
      const yKey = Math.round(item.y0 / 3) * 3;
      if (!dataLines.has(yKey)) dataLines.set(yKey, []);
      dataLines.get(yKey).push(item);
    }

    const rawRows = [];
    for (const [, items] of [...dataLines.entries()].sort(
      ([a], [b]) => a - b,
    )) {
      const row = {};
      for (const item of items) {
        const col = assignColumn(item.x0);
        row[col] = row[col] ? row[col] + " " + item.text : item.text;
      }
      rawRows.push(row);
    }

    // Merge continuation rows
    const merged = [];
    let current = null;
    for (const row of rawRows) {
      if (parseNum(row.no) !== null) {
        if (current) merged.push(current);
        current = { ...row };
      } else if (current) {
        for (const [k, v] of Object.entries(row)) {
          if (v?.trim())
            current[k] = current[k] ? current[k] + " " + v.trim() : v.trim();
        }
      }
    }
    if (current) merged.push(current);
    return merged;
  };

  // ═══════════════════════════════════════════════════════
  //  PROCESSING LAYER
  // ═══════════════════════════════════════════════════════

  const parseMaterialCost = (text) => {
    if (!text) return { unitCost: null, uom: "EA" };
    const str = text.toString().replace(/\$/g, "").trim();
    const pieceMatch = str.match(/([\d,]+\.?\d*)\s*\/\s*Piece/i);
    if (pieceMatch)
      return {
        unitCost: parseFloat(
          parseFloat(pieceMatch[1].replace(/,/g, "")).toFixed(4),
        ),
        uom: "PIECE",
      };
    const packMatch = str.match(
      /([\d,]+\.?\d*)\s*\/\s*(Bag|Roll|Tube|Box|Can|Carton|Pail|Drum)/i,
    );
    if (packMatch)
      return {
        unitCost: parseFloat(
          parseFloat(packMatch[1].replace(/,/g, "")).toFixed(4),
        ),
        uom: packMatch[2].toUpperCase(),
      };
    const costMatch = str.match(
      /([\d,]+\.?\d*)\s*\/?\s*([\d,]+\.?\d*)\s*(SF|LF|SY|EA|LS)?/i,
    );
    let unitCost = null,
      uom = "EA";
    if (costMatch) {
      const num1 = parseFloat(costMatch[1].replace(/,/g, "")) || 0;
      const num2 = parseFloat(costMatch[2]?.replace(/,/g, "") || "1");
      unitCost = num2 === 0 ? 0 : num1 / num2;
      uom =
        costMatch[3]?.toUpperCase() ||
        (str.includes("SF")
          ? "SF"
          : str.includes("LF")
            ? "LF"
            : str.includes("SY")
              ? "SY"
              : "EA");
    } else {
      const direct = parseFloat(str.replace(/[^\d.]/g, ""));
      if (!isNaN(direct) && direct > 0) unitCost = direct;
    }
    return { unitCost: unitCost ? parseFloat(unitCost.toFixed(4)) : null, uom };
  };

  const parseCoverage = (sizeStr, uom) => {
    if (!sizeStr) return null;
    const upperUom = (uom || "").toUpperCase();
    if (!["SF", "LF"].includes(upperUom)) return null;

    const s = sizeStr.toString();
    const bracketMatch = s.match(/\(\s*([^)]+?)\s*\)/);
    const finalText =
      (bracketMatch ? bracketMatch[1].trim() : null) || s.trim();

    const parseFeetInches = (str) => {
      const clean = str.replace(/[^0-9'."-]/g, "");
      const match = clean.match(
        /^(\d+\.?\d*)\s*'\s*(?:-?\s*(\d+\.?\d*))?\s*(?:"?|$)/i,
      );
      if (!match) return null;
      const feet = parseFloat(match[1]);
      const inches = match[2] ? parseFloat(match[2]) : NaN;
      return parseFloat((feet + (isNaN(inches) ? 0 : inches / 12)).toFixed(6));
    };

    if (upperUom === "SF") {
      const areaMatch = finalText.match(/([\d.'"-]+)\s*[xX×]\s*([\d.'"-]+)/i);
      if (areaMatch) {
        const w = parseFeetInches(areaMatch[1]);
        const h = parseFeetInches(areaMatch[2]);
        if (w !== null && h !== null && w > 0 && h > 0)
          return parseFloat((w * h).toFixed(4));
      }
    }

    if (upperUom === "LF") {
      const linear = parseFeetInches(finalText);
      if (linear !== null) return parseFloat(linear.toFixed(4));
    }

    return null;
  };

  const processConditionItems = (rawRows, conditionName, currentArea) => {
    const groupMap = new Map();
    for (const raw of rawRows) {
      const rawQty = raw.quantity || "";
      if (!rawQty || !/[\d.,-]/.test(rawQty)) continue;
      const hasLabor = raw.laborCode?.trim().length > 0;
      let itemTotalValue = hasLabor
        ? (parseNum(raw.itemTotal) ?? parseNum(raw.labTotal) ?? null)
        : (parseNum(raw.itemTotal) ?? parseNum(raw.matTotal) ?? null);
      if (itemTotalValue === null || isNaN(itemTotalValue)) continue;

      const section = (raw.section || "").trim();
      const matCode = (raw.matCode || "").trim();
      const labCode = (raw.laborCode || "").trim();
      const rawSize = raw.size || "";
      const size = cleanSize(rawSize);
      const rawUoM = (raw.uom || "").trim().toUpperCase();
      const crewHours = parseNum(raw.crewHours) || 0;
      const materialCostStr = (raw.materialCost || "").trim() || "-";
      const laborCostStr = (raw.laborCost || "").trim();
      const matTotalFromFile = parseNum(raw.matTotal) || 0;
      const labTotalFromFile = parseNum(raw.labTotal) || 0;
      const key = `${section}|${matCode}|${labCode}|${size}`;

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          bestItemTotal: itemTotalValue,
          matCode,
          labCode,
          size,
          rawSize,
          quantity: 0,
          section,
          rawUoM,
          crewHours,
          materialCostStr,
          laborCostStr,
          matTotalFromFile,
          labTotalFromFile,
        });
      }
      const entry = groupMap.get(key);
      const qtyMatch = rawQty.match(/(-?[\d,]+\.?\d*)/);
      if (qtyMatch)
        entry.quantity += parseFloat(qtyMatch[1].replace(/,/g, "") || 0);
      if (Math.abs(itemTotalValue) > Math.abs(entry.bestItemTotal)) {
        Object.assign(entry, {
          bestItemTotal: itemTotalValue,
          rawUoM,
          crewHours,
          materialCostStr,
          laborCostStr,
          matTotalFromFile,
          labTotalFromFile,
          rawSize,
        });
      }
    }

    const items = [];
    for (const entry of groupMap.values()) {
      const { unitCost: parsedUnitCost, uom: parsedUom } = parseMaterialCost(
        entry.materialCostStr,
      );
      const coverage = parseCoverage(
        entry.rawSize,
        parsedUom || entry.rawUoM || "EA",
      );
      const hasLabor =
        entry.labCode && entry.labCode.trim() !== "" && entry.labCode !== "-";
      const category = hasLabor ? "L" : "M";
      const finalUoM =
        category === "L" ? entry.rawUoM || "EA" : parsedUom || "EA";

      let reqQty,
        finalMatTotal,
        finalLabTotal,
        finalItemTotal,
        finalMaterialCost,
        finalCoverage,
        finalUnitCost;

      if (category === "M") {
        const packUoms = [
          "BOX",
          "PIECE",
          "ROLL",
          "BAG",
          "CAN",
          "CARTON",
          "TUBE",
          "PAIL",
          "DRUM",
        ];
        const isPackItem = packUoms.includes(finalUoM.toUpperCase());
        const sign = Math.sign(entry.quantity) || 1;

        if (
          isPackItem &&
          parsedUnitCost > 0 &&
          Math.abs(entry.matTotalFromFile) > 0
        ) {
          const req = Math.ceil(
            Math.abs(entry.matTotalFromFile) / parsedUnitCost,
          );
          reqQty = sign * req;
          finalMatTotal = Math.round(reqQty * parsedUnitCost * 100) / 100;
        } else if (finalUoM === "SF" || finalUoM === "LF") {
          const absQty = Math.abs(entry.quantity);
          if (coverage && coverage > 0 && absQty > 0) {
            const calc = absQty / coverage;
            const dec = calc - Math.floor(calc);
            reqQty =
              sign *
              (dec >= 0.5 ? Math.ceil(calc) : Math.max(1, Math.floor(calc)));
          } else {
            reqQty = sign;
          }
          finalMatTotal =
            parsedUnitCost !== null && coverage > 0
              ? Math.round(reqQty * parsedUnitCost * coverage * 100) / 100
              : entry.matTotalFromFile;
        } else if (
          finalUoM === "EA" &&
          parsedUnitCost > 0 &&
          Math.abs(entry.matTotalFromFile) > 0
        ) {
          const calc = Math.abs(entry.matTotalFromFile) / parsedUnitCost;
          const dec = calc - Math.floor(calc);
          reqQty =
            sign *
            (dec >= 0.5 ? Math.ceil(calc) : Math.max(1, Math.floor(calc)));
          finalMatTotal = Math.round(reqQty * parsedUnitCost * 100) / 100;
        } else {
          reqQty = sign;
          finalMatTotal = entry.matTotalFromFile;
        }
        finalItemTotal = finalMatTotal;
        finalLabTotal = 0;
        finalMaterialCost = entry.materialCostStr;
        finalCoverage = coverage;
        finalUnitCost = parsedUnitCost;
      } else {
        reqQty = null;
        finalCoverage = null;
        finalMatTotal = 0;
        finalMaterialCost = "";
        finalUnitCost = entry.laborCostStr
          ? parseFloat(entry.laborCostStr.replace(/,/g, "")) || 0
          : 0;
        finalLabTotal = Math.round(entry.quantity * finalUnitCost * 100) / 100;
        finalItemTotal = finalLabTotal;
      }

      items.push({
        section: entry.section,
        matCode: entry.matCode || "-",
        labCode: entry.labCode || "-",
        size: entry.size.trim(),
        materialCost: finalMaterialCost,
        quantity: entry.quantity,
        uom: finalUoM,
        unitCost: finalUnitCost,
        coverage: finalCoverage,
        reqQty,
        category,
        matTotal: finalMatTotal,
        labTotal: finalLabTotal,
        itemTotal: finalItemTotal,
        conditionName,
        area: currentArea,
        crewHours: entry.crewHours || 0,
        matStartsWithD: /^D/i.test(entry.matCode.trim()),
        matContainsS: /S/i.test(entry.matCode),
        matContainsT: /T/i.test(entry.matCode),
        matContainsCH: /CH/i.test(entry.matCode),
        matStartsWithES: /^ES/i.test(entry.matCode.trim()),
        matContainsTT: /TT/i.test(entry.matCode),
        matStartsWithI: /^I/i.test(entry.matCode.trim()),
        labStartsWithD: /^D/i.test(entry.labCode.trim()),
        labStartsWithTL: /^TL/i.test(entry.labCode.trim()),
        labStartsWithES: /^ES/i.test(entry.matCode.trim()),
        labStartsWithS: /^S/i.test(entry.labCode.trim()),
        labStartsWithT: /^T/i.test(entry.labCode.trim()),
        labStartsWithCH: /^CCH/i.test(entry.labCode.trim()),
        labStartsWithTT: /^TT/i.test(entry.labCode.trim()),
        labStartsWithI: /^I/i.test(entry.labCode.trim()),
        isLabor: category === "L",
        isAreaMaterial: category === "M",
        laborId: undefined,
      });
    }

    // Labor linking — unchanged
    const drywallSections = ["09 29 00", "09 29 15"],
      acousticalSection = "09 22 16",
      insulationSections = ["09 29 00", "07 21 00"];
    let nextLaborId = 1;
    const drywallMats = items.filter(
      (i) =>
        i.isAreaMaterial &&
        drywallSections.includes(i.section) &&
        i.matStartsWithD,
    );
    const dLabor = items.filter((i) => i.isLabor && i.labStartsWithD);
    const tlLabor = items.filter(
      (i) => i.isLabor && i.labStartsWithTL && i.section === "09 29 15",
    );
    if (drywallMats.length > 0 && (dLabor.length > 0 || tlLabor.length > 0)) {
      const id = `L${nextLaborId++}`;
      drywallMats.forEach((m) => (m.laborId = id));
      dLabor.forEach((l) => (l.laborId = id));
      tlLabor.forEach((l) => (l.laborId = id));
    }
    for (const laborItem of items.filter(
      (i) => i.isLabor && i.section === acousticalSection && i.labStartsWithS,
    )) {
      const mats = items.filter(
        (m) =>
          m.isAreaMaterial &&
          m.section === laborItem.section &&
          m.matContainsS &&
          !m.matContainsT,
      );
      if (mats.length) {
        const id = `L${nextLaborId++}`;
        laborItem.laborId = id;
        mats.forEach((m) => (m.laborId = id));
      }
    }
    for (const laborItem of items.filter(
      (i) => i.isLabor && i.section === acousticalSection && i.labStartsWithT,
    )) {
      const mats = items.filter(
        (m) =>
          m.isAreaMaterial && m.section === laborItem.section && m.matContainsT,
      );
      if (mats.length) {
        const id = `L${nextLaborId++}`;
        laborItem.laborId = id;
        mats.forEach((m) => (m.laborId = id));
      }
    }
    for (const laborItem of items.filter(
      (i) => i.isLabor && i.section === acousticalSection && i.labStartsWithCH,
    )) {
      const mats = items.filter(
        (m) =>
          m.isAreaMaterial &&
          m.section === laborItem.section &&
          m.matContainsCH,
      );
      if (mats.length) {
        const id = `L${nextLaborId++}`;
        laborItem.laborId = id;
        mats.forEach((m) => (m.laborId = id));
      }
    }
    for (const laborItem of items.filter(
      (i) => i.isLabor && i.labStartsWithI,
    )) {
      const mats = items.filter(
        (m) =>
          m.isAreaMaterial &&
          insulationSections.includes(m.section) &&
          m.matStartsWithI,
      );
      if (mats.length) {
        const id = `L${nextLaborId++}`;
        laborItem.laborId = id;
        mats.forEach((m) => (m.laborId = id));
      }
    }
    for (const laborItem of items.filter(
      (i) => i.isLabor && i.labStartsWithES,
    )) {
      const mats = items.filter((m) => m.isAreaMaterial && m.matStartsWithES);
      if (mats.length) {
        const id = `L${nextLaborId++}`;
        laborItem.laborId = id;
        mats.forEach((m) => (m.laborId = id));
      }
    }
    for (const laborItem of items.filter(
      (i) => i.isLabor && i.labStartsWithTT,
    )) {
      const mats = items.filter((m) => m.isAreaMaterial && m.matContainsTT);
      if (mats.length) {
        const id = `L${nextLaborId++}`;
        laborItem.laborId = id;
        mats.forEach((m) => (m.laborId = id));
      }
    }
    for (const laborItem of items.filter(
      (i) => i.isLabor && /^PLY/i.test(i.labCode.trim()),
    )) {
      const mats = items.filter(
        (m) =>
          m.isAreaMaterial &&
          m.section === laborItem.section &&
          /^PLY/i.test(m.matCode.trim()),
      );
      if (mats.length) {
        const id = `L${nextLaborId++}`;
        laborItem.laborId = id;
        mats.forEach((m) => (m.laborId = id));
      }
    }
    for (const laborItem of items.filter(
      (i) => i.isLabor && !i.laborId && i.labStartsWithD && !i.labStartsWithTL,
    )) {
      const mats = items.filter(
        (m) =>
          m.isAreaMaterial &&
          m.section === laborItem.section &&
          m.matStartsWithD,
      );
      if (mats.length) {
        const id = `L${nextLaborId++}`;
        laborItem.laborId = id;
        mats.forEach((m) => (m.laborId = id));
      }
    }
    return items;
  };

  // ═══════════════════════════════════════════════════════
  //  AGGREGATION — combine rows by (Category + Code + Area)
  //  "Code" = Mat. Code for M, Labor Code for L
  //  Removes Condition and Labor ID from final output
  // ═══════════════════════════════════════════════════════

  const aggregateItems = (allProcessed) => {
    // Key: category | primaryCode | area  (+ section + size for M to keep variants separate)
    // For M: group by matCode + area + section + size — same physical SKU in the same floor
    // For L: group by labCode + area + section — same labour task on same floor
    const aggMap = new Map();

    for (const item of allProcessed) {
      const code = item.category === "M" ? item.matCode : item.labCode;
      // Include size in key for M (different sizes are different products)
      const key =
        item.category === "M"
          ? `M|${code}|${item.area}|${item.section}|${item.size}`
          : `L|${code}|${item.area}|${item.section}`;

      if (!aggMap.has(key)) {
        aggMap.set(key, {
          // Identity fields (kept from first occurrence)
          category: item.category,
          section: item.section,
          matCode: item.matCode,
          labCode: item.labCode,
          size: item.size,
          uom: item.uom,
          unitCost: item.unitCost,
          coverage: item.coverage,
          materialCost: item.materialCost,
          area: item.area,
          description: item.description || "",
          // Accumulated fields
          quantity: 0,
          reqQty: 0,
          matTotal: 0,
          labTotal: 0,
          itemTotal: 0,
          hours: 0,
          _hasReqQty: false,
        });
      }

      const agg = aggMap.get(key);
      agg.quantity += item.quantity || 0;
      agg.matTotal += item.matTotal || 0;
      agg.labTotal += item.labTotal || 0;
      agg.itemTotal += item.itemTotal || 0;
      agg.hours += item.crewHours || 0;

      // reqQty: only meaningful for M items
      if (
        item.category === "M" &&
        item.reqQty !== null &&
        item.reqQty !== undefined
      ) {
        agg.reqQty += Number(item.reqQty) || 0;
        agg._hasReqQty = true;
      }

      // Keep the most specific description
      if (!agg.description && item.description)
        agg.description = item.description;
    }

    // Re-number and build output rows (no Condition, no Labor ID)
    let itemNum = 1;
    const rows = [];
    for (const agg of aggMap.values()) {
      const finalReqQty =
        agg._hasReqQty && agg.category === "M" ? agg.reqQty : null;
      rows.push({
        "Item #": itemNum++,
        Category: agg.category,
        Section: agg.section,
        "Mat. Code": agg.matCode,
        "Labor Code": agg.labCode,
        Description: agg.description,
        Size: agg.size,
        Quantity: parseFloat(agg.quantity.toFixed(2)),
        UoM: agg.uom || "-",
        "Unit Cost": agg.unitCost > 0 ? Number(agg.unitCost).toFixed(2) : "-",
        "Coverage/Unit": agg.coverage ? Number(agg.coverage).toFixed(4) : "-",
        "Req Qty": finalReqQty !== null ? finalReqQty : "-",
        "Order As": finalReqQty !== null ? `${finalReqQty} ${agg.uom}` : "-",
        "Material Cost": agg.materialCost || "-",
        "Mat. Total": parseFloat(agg.matTotal.toFixed(2)),
        "Labor Total": parseFloat(agg.labTotal.toFixed(2)),
        "Item Total": parseFloat(agg.itemTotal.toFixed(2)),
        Hours: parseFloat(agg.hours.toFixed(2)),
        Area: agg.area,
      });
    }

    return rows;
  };

  // ═══════════════════════════════════════════════════════
  //  MAIN: Parse PDF → Process → Aggregate → Excel
  // ═══════════════════════════════════════════════════════

  const parsePdf = async (file) => {
    setLoading(true);
    setResult(null);
    setProgress("Loading Item Database...");

    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      // ── Step 1: Item Database ──────────────────────────────────────────
      // ── Step 1: Item Database ──────────────────────────────────────────
      const itemDbMap = new Map();
      try {
        const dbRes = await axios.get(`${API_BASE}/item-database?limit=5000`, {
          headers,
        });
        const dbItems = dbRes.data.items || [];
        for (const dbItem of dbItems) {
          if (dbItem.itemCode && dbItem.description) {
            const key = dbItem.itemCode.trim().toUpperCase();
            const spacelessKey = key.replace(/\s+/g, "");
            // Store both description AND sectionNumber
            const entry = {
              description: dbItem.description,
              sectionNumber: dbItem.sectionNumber || null,
            };
            itemDbMap.set(key, entry);
            if (spacelessKey !== key) itemDbMap.set(spacelessKey, entry);
          }
        }
        setProgress(`Loaded ${itemDbMap.size} items. Parsing PDF...`);
      } catch (dbErr) {
        console.warn("Item Database unavailable:", dbErr.message);
        setProgress("Item Database unavailable. Parsing PDF...");
      }

      // ── Step 2: Parse all pages ────────────────────────────────────────
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const totalPages = pdf.numPages;

      const allProcessed = [];

      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        if (pageNum % 50 === 0 || pageNum === 1)
          setProgress(`Processing page ${pageNum} of ${totalPages}...`);

        const page = await pdf.getPage(pageNum);
        const textItems = await extractPageTextItems(page);
        if (textItems.length === 0) continue;

        const meta = parseMetadataFromItems(textItems);
        const rawRows = parseTableFromItems(textItems);
        if (rawRows.length === 0) continue;

        const conditionName =
          `${meta.conditionNo} - ${meta.name}`.trim() || "Unknown";
        const currentArea = meta.area || "Unknown";

        const processed = processConditionItems(
          rawRows,
          conditionName,
          currentArea,
        );

        for (const item of processed) {
          const rawCode =
            item.category === "L"
              ? (item.labCode || "").trim().toUpperCase()
              : (item.matCode || "").trim().toUpperCase();

          const spacelessCode = rawCode.replace(/\s+/g, "");

          const exactMatch = itemDbMap.get(rawCode);
          const spacelessMatch = !exactMatch
            ? itemDbMap.get(spacelessCode)
            : null;
          const dbEntry = exactMatch || spacelessMatch || null;

          item.description = dbEntry?.description || "";

          // Override section with DB value if available
          if (dbEntry?.sectionNumber) {
            item.section = dbEntry.sectionNumber;
          }

          // If we only matched via spaceless key, normalize the code on the item
          if (!exactMatch && spacelessMatch) {
            if (item.category === "L") {
              item.labCode = spacelessCode;
            } else {
              item.matCode = spacelessCode;
            }
          }
        }
        allProcessed.push(...processed);
      }

      // ── Step 3: Aggregate ─────────────────────────────────────────────
      setProgress("Aggregating rows...");
      const aggregated = aggregateItems(allProcessed);

      const grandTotal = aggregated.reduce((s, i) => s + i["Item Total"], 0);
      const matchedCount = aggregated.filter((i) => i.Description).length;

      // ── Step 4: Find unmatched ────────────────────────────────────────
      const unmatchedItems = aggregated
        .filter((i) => !i.Description || i.Description.trim() === "")
        .map((i) => ({
          category: i.Category,
          code: i.Category === "M" ? i["Mat. Code"] : i["Labor Code"],
          section: i.Section,
          area: i.Area,
        }))
        // Deduplicate by code
        .filter(
          (item, index, self) =>
            index === self.findIndex((t) => t.code === item.code),
        );

      setResult({
        items: aggregated,
        grandTotal: Math.round(grandTotal),
        rowCount: aggregated.length,
        rawRowCount: allProcessed.length,
        dbCount: itemDbMap.size,
        matchedCount,
        unmatchedItems,
        confirmed: false,
      });
      setProgress("");
    } catch (err) {
      console.error(err);
      alert("Error: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadExcel = () => {
    if (!result) return;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.json_to_sheet(result.items);

    // Column widths
    ws["!cols"] = [
      { wch: 8 }, // Item #
      { wch: 6 }, // Category
      { wch: 12 }, // Section
      { wch: 14 }, // Mat. Code
      { wch: 14 }, // Labor Code
      { wch: 40 }, // Description
      { wch: 18 }, // Size
      { wch: 10 }, // Quantity
      { wch: 8 }, // UoM
      { wch: 12 }, // Unit Cost
      { wch: 14 }, // Coverage/Unit
      { wch: 10 }, // Req Qty
      { wch: 14 }, // Order As
      { wch: 16 }, // Material Cost
      { wch: 12 }, // Mat. Total
      { wch: 12 }, // Labor Total
      { wch: 12 }, // Item Total
      { wch: 8 }, // Hours
      { wch: 16 }, // Area
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Budget");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(new Blob([buf]), `Budget_${result.grandTotal}.xlsx`);
  };

  return (
    <div className="p-8 max-w-4xl mx-auto text-center">
      <h1 className="text-5xl font-black mb-8 text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-pink-600">
        PDF → BUDGET EXPORT
      </h1>
      <p className="text-sm uppercase tracking-[4px] mb-8 text-zinc-400">
        PDF UPLOAD · AGGREGATE BY CODE + AREA · NO CONDITIONS
      </p>

      <label className="block mx-auto text-3xl file:mr-12 file:py-12 file:px-32 file:rounded-full file:bg-gradient-to-r file:from-yellow-400 file:to-pink-600 file:text-black file:text-4xl font-black cursor-pointer">
        UPLOAD CONDITION PDF
        <input
          type="file"
          accept=".pdf"
          onChange={(e) => e.target.files?.[0] && parsePdf(e.target.files[0])}
          className="hidden"
        />
      </label>

      {loading && (
        <div className="mt-12 text-2xl font-mono text-pink-500 animate-pulse">
          {progress || "Processing..."}
        </div>
      )}

      {result && result.unmatchedItems.length > 0 && !result.confirmed && (
        <div className="mt-12 space-y-4">
          <div className="text-3xl font-black text-yellow-400">
            {result.unmatchedItems.length} UNMATCHED ITEM
            {result.unmatchedItems.length !== 1 ? "S" : ""}
          </div>
          <p className="text-zinc-400 text-sm max-w-lg mx-auto">
            These codes have no description in the Item Database. Add them
            first, then re-upload for a complete export.
          </p>
          <div className="text-left bg-zinc-900 rounded-xl p-4 max-h-64 overflow-y-auto space-y-2 mx-auto max-w-lg">
            {result.unmatchedItems.map((item, i) => (
              <div
                key={i}
                className="flex gap-4 text-sm font-mono border-b border-zinc-800 pb-2 last:border-0 last:pb-0"
              >
                <span className="text-pink-400 w-6">{item.category}</span>
                <span className="text-yellow-300 w-44 truncate">
                  {item.code}
                </span>
                <span className="text-zinc-500 truncate">
                  {item.section} · {item.area}
                </span>
              </div>
            ))}
          </div>
          <button
            onClick={() => setResult((r) => ({ ...r, confirmed: true }))}
            className="mt-4 px-8 py-3 border border-zinc-700 text-zinc-500 rounded-xl text-sm hover:border-zinc-400 hover:text-zinc-300 transition"
          >
            Export anyway
          </button>
        </div>
      )}

      {result && (result.confirmed || result.unmatchedItems.length === 0) && (
        <div className="mt-12 space-y-4">
          <div className="text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-emerald-400 to-cyan-400">
            ${result.grandTotal.toLocaleString()}
          </div>
          <div className="text-xl font-mono tracking-widest text-emerald-400">
            {result.rowCount} AGGREGATED ROWS
          </div>
          <div className="text-sm text-zinc-500">
            Collapsed from {result.rawRowCount} raw items ·{" "}
            {result.matchedCount} of {result.rowCount} matched to database (
            {result.dbCount} items loaded)
          </div>
          <div className="text-xs text-zinc-600">
            Grouped by: Category + Mat./Labor Code + Area + Section + Size
          </div>
          <button
            onClick={downloadExcel}
            className="mt-6 px-16 py-6 bg-gradient-to-r from-yellow-400 to-pink-600 text-black font-black text-3xl rounded-2xl hover:scale-105 transition-transform"
          >
            DOWNLOAD EXCEL
          </button>
        </div>
      )}
    </div>
  );
}
